import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { JbCart, JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { ShopifyStorefrontClient } from '../../../api/shopify/ShopifyStorefrontClient';
import { trackRemoveFromCartShopify } from '../trackRemoveFromCartShopify';
import { trackViewCartShopify } from '../trackViewCartShopify';
import { hasReachedMaxQty } from '../../checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../../checkMaxQty/showMaxQtyAlert';
import { getMarketplacePropertiesFromExistingItem } from '../refreshTieredProductDetails';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from '../../shopify/getStoreCurrency';
import { transformCartItemForMessages, transformCartItemForTracking } from '../transformCartItemForTracking';
import { delayWithCancel } from '@jbhi-fi/utils/src/delay';
import { getProductServiceVariantId } from '../../../templates/components/pdp/utils/product-service';
import { addServiceOffering } from '../addServiceOffering';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { ServiceOffering } from '@jbhi-fi/apis/src/services/types';
import { removeAttachedServiceOffering } from '../removeAttachedServiceOffering';
import { dataLayerPushServiceEvent } from '../../../templates/components/pdp/utils/utils';
import { ProductGtmData } from '@jbhi-fi/apis/src/algolia/types/ProductGtmData';
import {
  getCartLoading,
  getCartState,
  setCartLoading,
  updateCart,
  CART_UPDATE_EVENT,
  validateCart,
  updateCartState,
} from '../cartUtils';
import { setServiceOfferings } from '../../parseServiceOfferings';

const storefrontClient = new ShopifyStorefrontClient();

export interface CartContextInterface {
  cart: JbCart | undefined;
  location: 'cart' | 'checkout' | 'minicart';
  removeItem: (item: JbCartItem, shouldShowWishlistCard?: boolean) => Promise<void>;
  clearWishlistCards: () => void;
  updateQuantity: (item: JbCartItem, quantity: number) => Promise<void>;
  addService: (primaryItem: JbCartItem, offeredService: ServiceOffering) => Promise<void>;
  removeAttachedService: (item: JbCartItem, message?: string) => Promise<void>;
  addBundleOffering: (item: JbCartItem) => Promise<void>;
  isMiniCartOpen: boolean;
  toggleMiniCart: (isOpen: boolean) => void;
}

export const CartContext = createContext<CartContextInterface | undefined>(undefined);

const getLocation = () => {
  if (window.location.pathname === '/cart') return 'cart';
  if (window.Shopify?.Checkout) return 'checkout';
  return 'minicart';
};

export const CartContextProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [cart, setCart] = useState<JbCart | undefined>(getCartState);
  const [isMiniCartOpen, setIsMiniCartOpen] = useState<boolean>(false);
  const location = getLocation();
  const wishlistTimeouts = useRef<Array<() => void>>([]);
  const wishlistRemoveTimeout = window.Jbhifi.cartWishlistTimeout * 1000;

  const clearWishlistCards = () => {
    wishlistTimeouts.current.forEach(cancel => cancel());
    wishlistTimeouts.current = [];
  };

  const toggleMiniCart = (isOpen: boolean) => {
    if (isOpen) {
      trackViewCartShopify(cart, 'minicart');
    }
    setIsMiniCartOpen(isOpen);
  };

  useEffect(() => {
    const handleCartUpdate = (event: CustomEventInit<JbCart | undefined>) => {
      event.detail && setCart(event.detail);
    };
    window.addEventListener(CART_UPDATE_EVENT, handleCartUpdate);
    return () => {
      window.removeEventListener(CART_UPDATE_EVENT, handleCartUpdate);
    };
  }, []);

  const removeItem = async (item: JbCartItem, shouldShowWishlistCard?: boolean) => {
    if (getCartLoading()) return;

    clearWishlistCards();
    setCartLoading(true);
    const updatedCart = await storefrontClient.removeFromCart(item.line_id);
    const { validatedCart, cartApiResponse } = await validateCart(updatedCart);
    const newCart = validatedCart || updatedCart;
    setCartLoading(false);

    if (shouldShowWishlistCard) {
      setCart(prevCart => {
        if (!prevCart) return prevCart;
        return {
          ...prevCart,
          items: prevCart.items.map(cartItem =>
            cartItem.line_id === item.line_id ? { ...cartItem, removed: true } : cartItem
          ),
        };
      });
      const delay = delayWithCancel(wishlistRemoveTimeout);
      wishlistTimeouts.current.push(delay.cancel);
      await delay.promise;
    }
    setServiceOfferings(cartApiResponse, newCart);
    updateCartState(newCart);

    trackRemoveFromCartShopify(item);
    trackViewCartShopify(updatedCart, location);
  };

  const updateQuantity = async (item: JbCartItem, quantity: number) => {
    if (getCartLoading()) return;
    clearWishlistCards();
    const prevQuantity = item.quantity;
    const quantityChange = quantity - prevQuantity;
    if (hasReachedMaxQty(item?.id, quantityChange)) {
      showMaxQtyAlert();
      return;
    }
    setCartLoading(true);
    let updatedCart;
    if (prevQuantity === 1) {
      // We don't copy service related attributes when increasing quantity
      const { _serviceLink, _isAttachedService, _legacy_addons_line_key, ...properties } = item.properties;
      const marketplaceProperties = getMarketplacePropertiesFromExistingItem(item);
      updatedCart = await storefrontClient.addMultipleToCart(
        Array.from({ length: quantityChange }, () => ({
          variantId: item.id,
          attributes: filterAndMapAttributes({ ...properties, ...marketplaceProperties }),
        }))
      );
    } else {
      updatedCart = await storefrontClient.updateCartLine(item.line_id, quantity);
    }
    const { validatedCart } = await updateCart(updatedCart);
    if (quantityChange > 0) {
      trackAddToCart({
        location: location,
        currency: getStoreCurrency(),
        addedItems: transformCartItemForTracking({
          ...item,
          quantity: quantityChange,
        }),
        message: transformCartItemForMessages(item),
      });
    } else if (quantityChange < 0) {
      trackRemoveFromCartShopify(item, Math.abs(quantityChange));
    }
    trackViewCartShopify(validatedCart, location);
  };

  const addService = async (primaryItem: JbCartItem, offeredService: ServiceOffering) => {
    if (getCartLoading()) return;
    clearWishlistCards();
    const variantId = await getProductServiceVariantId(offeredService.sku);
    if (!variantId) return;

    await addServiceOffering({
      variantId,
      location,
      serviceData: offeredService,
      primaryLineItem: primaryItem,
      message: transformCartItemForMessages(primaryItem),
    });
  };

  const removeAttachedService = async (item: JbCartItem) => {
    if (getCartLoading()) return;
    clearWishlistCards();

    const attachedService = item.attachedService;
    if (!attachedService) return;

    const serviceData = tryParseJson<ServiceOffering>(attachedService?.properties._serviceData);
    if (!serviceData) return;

    const gtmTrackingData = tryParseJson<ProductGtmData>(attachedService?.properties?._gtmProduct || '{}');

    setCartLoading(true);
    await removeAttachedServiceOffering(attachedService.line_id, attachedService.properties._serviceLink);
    dataLayerPushServiceEvent(
      `${serviceData.typeId}.removedfromcart`,
      gtmTrackingData,
      {
        sku: serviceData.sku,
        name: serviceData.offeringName,
        price: item.line_price / 100,
      },
      location,
      transformCartItemForMessages(item)
    );
  };

  const addBundleOffering = async (item: JbCartItem) => {
    const suggestion = item.bundleSuggestions?.[0];
    if (!suggestion) return;
    setCartLoading(true);
    const properties = {
      _availabilityCanPreOrder: suggestion.availability?.canPreOrder,
      _availabilityFulfilment: suggestion.availability?.fulfilment,
      _maxQty: suggestion.availability.fulfilment === 'Digital' ? 1 : suggestion.product?.limitPerOrder,
      _itemListName: 'Bundle buy',
      _priceAtc: Math.round(suggestion.price * 100),
    };
    const updatedCart = await storefrontClient.addToCart(suggestion.variant_id, 1, filterAndMapAttributes(properties));
    await updateCart(updatedCart);
    trackViewCartShopify(updatedCart, location);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        location,
        removeItem,
        clearWishlistCards,
        updateQuantity,
        addService,
        removeAttachedService,
        addBundleOffering,
        isMiniCartOpen,
        toggleMiniCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (context === undefined || context === null) {
    throw new Error('useCartContext must be used within a useCartContextProvider');
  }
  return context;
};
